import type { FC } from 'react';
import { PapiDeviation, PapiGallection } from '@wix/da-papi-types';
import { StudioSection } from '../../../../types/store';
import StudioBreadcrumbs from '../../../StudioBreadcrumbs';
import TierBreadcrumbs from '../../../TierBreadcrumbs';
import TierActionsDropdown from '../../../_partials/TierActionsDropdown';
import GalleryDisplaySettingsDropdown from '../../../_partials/GalleryDisplaySettingsDropdown';
import TierTabs from '../../../published/TierTabs';
import TierDescription from '../../../published/TierDescription';
import PublishedTabs from '../../../published/PublishedTabs/PublishedTabs';
import MyGalleriesBreadcrumbs from '../../../MyGalleriesBreadcrumbs';
import FolderActionsDropdown from '../../../_partials/FolderActionsDropdown';
import useCanCreateSubfolder from '../../../../hooks/useCanCreateSubfolder';
import FreeBoostsPromoBanner from '../../../FreeBoostsPromoBanner';
import BaseHeader from '../BaseHeader';

import s from './PublishedHeader.scss';

export interface Props {
  activeSection: StudioSection;
  className?: string;
  selectedFolder?: PapiGallection;
  selectedTierId?: number;
  availableTiers?: PapiDeviation[];
  hasSubfolders?: boolean;
  isRootFolder?: boolean;
  isMyGalleriesPage?: boolean;
  isTierPage?: boolean;
}

const PublishedHeader: FC<Props> = ({
  activeSection,
  className,
  selectedFolder,
  selectedTierId,
  availableTiers,
  isRootFolder,
  isTierPage,
}) => {
  const isMyGalleriesPage = activeSection === StudioSection.PUBLISHED_GALLERIES;
  const isMyTiersPage = activeSection === StudioSection.PUBLISHED_TIERS;

  let breadcrumbs = <StudioBreadcrumbs />;
  if (isTierPage) {
    breadcrumbs = <TierBreadcrumbs />;
  } else if (isMyTiersPage) {
    breadcrumbs = <MyGalleriesBreadcrumbs />;
  }

  const canCreateFolder = useCanCreateSubfolder(selectedFolder);
  let thisTier: PapiDeviation | undefined;
  if (selectedTierId) {
    thisTier = (availableTiers || []).find(
      tier => tier.tier?.tierId === selectedTierId
    );
  }

  const actions = [
    !isRootFolder && selectedFolder && (
      <FolderActionsDropdown
        key="folder-actions"
        folder={selectedFolder}
        withCreateFolder={canCreateFolder}
        withBookmark
      />
    ),
    thisTier && (
      <TierActionsDropdown
        key="tier-actions"
        tier={thisTier}
        iconSizeOverride="medium"
      />
    ),
    isMyGalleriesPage && (
      <GalleryDisplaySettingsDropdown
        key="gallery-settings"
        iconSizeOverride="medium"
      />
    ),
  ];

  return (
    <>
      <BaseHeader
        className={className}
        titleSlot={
          <>
            {breadcrumbs}
            {isTierPage && <TierTabs />}
            {isRootFolder && !isTierPage && <PublishedTabs />}
          </>
        }
        middleSlot={<FreeBoostsPromoBanner />}
        actions={actions.filter(item => item).length > 0 ? actions : undefined}
      />
      {thisTier && (
        <TierDescription tier={thisTier} className={s['tier-description']} />
      )}
    </>
  );
};

PublishedHeader.displayName = 'PublishedHeader';

export default PublishedHeader;
